import { format, isValid, parseISO } from "date-fns";

const MS_PER_DAY = 86_400_000;

/**
 * Adjusts project/climate-action `startDate` and `endDate` for display.
 *
 * These two fields are stored with a +1 day offset in the database (UTC midnight
 * stored as the following day). Every **display** site for these specific fields
 * must subtract one day before showing the date to the user. Use this single
 * helper so the adjustment is defined in exactly one place.
 *
 * **Scope:** Only call this for `startDate` / `endDate` on Project and ClimateAction
 * entities. Do not use it for other date fields (e.g. task due dates, milestone
 * dates, snapshot dates) — those do not carry the offset.
 */
export function adjustProjectDateForDisplay(date: Date | string | null | undefined): Date | null {
  if (!date) {
    return null;
  }
  const dateObj = date instanceof Date ? date : new Date(date);
  if (isNaN(dateObj.getTime())) {
    return null;
  }
  return new Date(dateObj.getTime() - MS_PER_DAY);
}

/**
 * Mirror of {@link adjustProjectDateForDisplay}: converts a date the user picked
 * back to the stored representation. Same scope — only `startDate` / `endDate` on
 * Project and ClimateAction.
 */
export function adjustProjectDateForPersistence(date: Date | string): Date {
  const dateObj = date instanceof Date ? date : new Date(date);
  return new Date(dateObj.getTime() + MS_PER_DAY);
}

/** Localized short date and time for display, e.g. table cells and detail views. */
export function formatDateTime(value: Date | string | null | undefined, locale = "de-DE"): string | null {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (!isValid(date)) {
    return null;
  }
  return date.toLocaleString(locale, { dateStyle: "short", timeStyle: "short" });
}

/** @deprecated Use {@link adjustProjectDateForDisplay} instead. */
export function adjustDateForCsvExport(date: Date | string | null | undefined): Date | null {
  return adjustProjectDateForDisplay(date);
}

export function formatDateForCsv(date: Date | string | null | undefined): string {
  if (!date) {
    return "NULL";
  }

  let dateObj: Date;
  if (date instanceof Date) {
    dateObj = date;
  } else {
    const parsed = parseISO(date);
    dateObj = isValid(parsed) ? parsed : new Date(date);
  }

  if (!isValid(dateObj)) {
    return "NULL";
  }

  return format(dateObj, "yyyy-MM-dd");
}
